import LocalStorageItem, {
  type LocalStorageKey,
} from '@lib/browsers/localStorage/LocalStorageItem'
import { isProcessClientSide } from '@lib/config/runtime'
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

const useLocalStorageState = <T>(
  key: LocalStorageKey,
  initialValue: T | (() => T),
) => {
  const [storedValue, setStoredValue] = useState(() => {
    return initialValue instanceof Function ? initialValue() : initialValue
  })

  const localStorageRef = useRef(new LocalStorageItem(key))

  const readValue = useCallback((): T => {
    const initialValueToUse =
      initialValue instanceof Function ? initialValue() : initialValue

    if (!isProcessClientSide()) {
      return initialValueToUse
    }

    const localStorageValue = localStorageRef.current.getItem<T>()

    return localStorageValue ?? initialValueToUse
  }, [initialValue, key])

  const setValue: Dispatch<SetStateAction<T>> = useCallback(
    (valueOrCallback) => {
      if (!isProcessClientSide()) {
        return
      }

      const value =
        valueOrCallback instanceof Function
          ? valueOrCallback(readValue())
          : valueOrCallback

      localStorageRef.current.setItem<T>(value)

      setStoredValue(value)
    },
    [key],
  )

  useEffect(() => {
    setValue(readValue())
  }, [])

  return [storedValue, setValue] as const
}

export default useLocalStorageState
